/**
 * Handles dynamic internationalization (i18n) logic for loading language catalogs
 * and updating static page elements with translated text using LinguiJS.
 *
 * - Dynamically loads and activates selected language translations.
 * - Updates footer elements with translations.
 *
 * @author Akash Salvi <akash.salvi@helpshift.com>
 * @created April 28, 2025
 * @module pages/internationalization/internationalizationLogic
 */

import {i18n} from "@lingui/core";
import {t} from "@lingui/core/macro";
import dom from "dom";
import {PREFERRED_LANG_LS_KEY} from "constants/pages";
import {DEFAULT_I18N_LANG} from "constants/modals/i18nLanguageOptions";
import {renderLangOptions} from "pages/internationalization/languageDropdown";

// Footer
const privacyPolicyLinkEl = dom.one("#privacy-policy-link");
const privacyPolicyText = dom.one("#privacy-policy-text");

/**
 * Loads and activates translations for the specified language using Lingui i18n.
 * Stores the selected language in localStorage. If an error occurs, falls back to English.
 *
 * @async
 * @param {string} lang - The locale code for the language to load (e.g., "en", "fr").
 * @returns {Promise<void>} A promise that resolves when the translation is loaded and activated.
 */
export const loadTranslations = async (lang) => {
  try {
    // Check if the requested language is already active
    if (i18n.locale === lang) {
      return;
    }

    // Import the locale-specific message catalog
    const catalogs = await import(`../../../locales/${lang}.mjs`);

    // Load and activate the locale in Lingui i18n
    i18n.load(lang, catalogs.messages);
    i18n.activate(lang);

    localStorage.setItem(PREFERRED_LANG_LS_KEY, lang);
  } catch (err) {
    // Load default lang file in case of any error
    await loadTranslations(DEFAULT_I18N_LANG);
    renderLangOptions(DEFAULT_I18N_LANG);

    // eslint-disable-next-line no-console
    console.error(`Error loading ${lang} translations. Please contact your provider.`);
  }
};

/**
 * Updates the footer's privacy policy text and link title with a localized string
 * for the Login, Set Up Account, and Message Box pages.
 */
export const updateFooter = () => {
  privacyPolicyText.getDOM().innerText = t`Privacy Policy`;
  privacyPolicyLinkEl.getDOM().title = t`Privacy Policy`;
};

export default i18n;
